import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import logging from "../utils/logging";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Clock, Save } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeLabels = [
  { key: "security", label: "Security" },
  { key: "fire", label: "Fire" },
  { key: "medical", label: "Medical" },
  { key: "accident", label: "Accident" },
  { key: "banditry", label: "Banditry" },
];

export default function EscalationPanel() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/escalation/rules`, { withCredentials: true });
      setRules(res.data);
    } catch (err) {
      logging.error("Failed to fetch escalation rules", err);
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const handleChange = (type, level, val) => {
    const num = Math.max(1, parseInt(val) || 1);
    setRules((prev) => ({
      ...prev,
      [type]: { ...prev[type], [level]: num },
    }));
  };

  const handleSave = async () => {
    if (!rules) return;
    setSaving(true);
    try {
      await axios.put(`${API}/escalation/rules`, {
        security_level_1: rules.security?.level_1 || 5,
        security_level_2: rules.security?.level_2 || 15,
        fire_level_1: rules.fire?.level_1 || 3,
        fire_level_2: rules.fire?.level_2 || 10,
        medical_level_1: rules.medical?.level_1 || 5,
        medical_level_2: rules.medical?.level_2 || 15,
        accident_level_1: rules.accident?.level_1 || 10,
        accident_level_2: rules.accident?.level_2 || 30,
      }, { withCredentials: true });
      toast.success("Escalation rules saved");
    } catch {
      toast.error("Failed to save rules");
    } finally {
      setSaving(false);
    }
  };

  if (!rules) return null;

  return (
    <div className="mt-5" data-testid="escalation-panel">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-3 flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" /> Escalation Rules
      </p>
      <div className="space-y-2">
        <div className="grid grid-cols-3 gap-1 text-[9px] uppercase tracking-widest font-bold text-gray-400 px-0.5">
          <span>Type</span>
          <span>Lvl 1 (min)</span>
          <span>Lvl 2 (min)</span>
        </div>
        {typeLabels.map(({ key, label }) => (
          <div key={key} className="grid grid-cols-3 gap-1 items-center">
            <span className="text-[11px] font-bold text-gray-700 font-body">{label}</span>
            <Input
              type="number"
              min={1}
              value={rules[key]?.level_1 || ""}
              onChange={(e) => handleChange(key, "level_1", e.target.value)}
              className="h-6 text-[11px] rounded-sm border-gray-300 px-1.5"
              data-testid={`escalation-${key}-l1`}
            />
            <Input
              type="number"
              min={1}
              value={rules[key]?.level_2 || ""}
              onChange={(e) => handleChange(key, "level_2", e.target.value)}
              className="h-6 text-[11px] rounded-sm border-gray-300 px-1.5"
              data-testid={`escalation-${key}-l2`}
            />
          </div>
        ))}
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="w-full mt-1 bg-black hover:bg-gray-800 text-white rounded-sm font-body text-[11px] h-7"
          data-testid="save-escalation-btn"
        >
          <Save className="w-3 h-3 mr-1" /> Save Rules
        </Button>
        <p className="text-[9px] text-gray-400 font-body">
          Auto-escalate unresolved alerts. Lvl 1 = Escalated, Lvl 2 = Critical (notifies all admins).
        </p>
      </div>
    </div>
  );
}
